<template>
  <div class="mod-page">
    <!-- Header -->
    <div class="mod-header">
      <div>
        <h1 class="mod-title">Sucursales</h1>
        <p class="mod-subtitle">Catálogo de sucursales</p>
      </div>
      <div class="mod-header-actions">
        <button class="mod-btn mod-btn-outline" @click="exportExcel">
          <i class="bi bi-file-earmark-excel"></i>
          <span>Exportar</span>
        </button>
        <button class="mod-btn mod-btn-outline" @click="sincronizarErp" :disabled="syncing">
          <span v-if="syncing" class="mod-spinner"></span>
          <i v-else class="bi bi-arrow-repeat"></i>
          <span>{{ syncing ? 'Sincronizando...' : 'Sincronizar ERP' }}</span>
        </button>
        <button class="mod-btn mod-btn-primary" @click="openForm()">
          <i class="bi bi-plus-lg"></i>
          <span>Nueva sucursal</span>
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="mod-filters">
      <div class="mod-search">
        <i class="bi bi-search"></i>
        <input
          type="text"
          v-model="searchText"
          placeholder="Buscar por clave o nombre..."
          @input="applyFilters"
        >
      </div>
      <div class="mod-filter-right">
        <select v-model="activoFilter" @change="applyFilters" class="mod-select">
          <option value="">Activos e inactivos</option>
          <option value="1">Activos</option>
          <option value="0">Inactivos</option>
        </select>
        <select v-model="pageSize" @change="applyFilters" class="mod-select">
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
        <span class="mod-filter-count">{{ totalFiltered }} registro{{ totalFiltered !== 1 ? 's' : '' }}</span>
      </div>
    </div>

    <!-- Tabla -->
    <div class="mod-table-wrap">
      <table class="mod-table">
        <thead>
          <tr>
            <th class="mod-th-sort" @click="sortBy('Clave')">
              Clave <i :class="sortIcon('Clave')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('Nombre')">
              Nombre <i :class="sortIcon('Nombre')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('EsDefault')">
              Predeterminada <i :class="sortIcon('EsDefault')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('EsActivo')">
              Activo <i :class="sortIcon('EsActivo')"></i>
            </th>
            <th class="mod-th-actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="5" class="mod-td-center">
              <div class="mod-loading"><span class="mod-spinner"></span> Cargando...</div>
            </td>
          </tr>
          <tr v-else-if="pagedItems.length === 0">
            <td colspan="5" class="mod-td-center mod-td-empty">
              <i class="bi bi-inbox"></i>
              <span>{{ searchText ? 'Sin resultados' : 'No hay sucursales registradas' }}</span>
            </td>
          </tr>
          <tr v-for="item in pagedItems" :key="item.Clave + '-' + item.EmpresaId" class="mod-tr">
            <td><span class="mod-badge">{{ item.Clave }}</span></td>
            <td>{{ item.Nombre }}</td>
            <td>
              <i v-if="item.EsDefault" class="bi bi-star-fill" style="color:#f6ad55;" title="Predeterminada"></i>
              <span v-else class="mod-td-muted">—</span>
            </td>
            <td>
              <span :class="item.EsActivo ? 'mod-status-active' : 'mod-status-inactive'">
                {{ item.EsActivo ? 'Si' : 'No' }}
              </span>
            </td>
            <td class="mod-td-actions">
              <button class="mod-btn-icon" title="Editar" @click="openForm(item)">
                <i class="bi bi-pencil"></i>
              </button>
              <button class="mod-btn-icon mod-btn-danger" title="Eliminar" @click="confirmDelete(item)">
                <i class="bi bi-trash3"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginacion -->
    <div class="mod-pagination" v-if="totalPages > 1">
      <button class="mod-page-btn" :disabled="currentPage === 1" @click="currentPage = 1">
        <i class="bi bi-chevron-double-left"></i>
      </button>
      <button class="mod-page-btn" :disabled="currentPage === 1" @click="currentPage--">
        <i class="bi bi-chevron-left"></i>
      </button>
      <template v-for="p in visiblePages" :key="p">
        <button
          class="mod-page-btn"
          :class="{ 'mod-page-active': p === currentPage }"
          @click="currentPage = p"
        >{{ p }}</button>
      </template>
      <button class="mod-page-btn" :disabled="currentPage === totalPages" @click="currentPage++">
        <i class="bi bi-chevron-right"></i>
      </button>
      <button class="mod-page-btn" :disabled="currentPage === totalPages" @click="currentPage = totalPages">
        <i class="bi bi-chevron-double-right"></i>
      </button>
      <span class="mod-page-info">Pagina {{ currentPage }} de {{ totalPages }}</span>
    </div>

    <!-- Modal Form -->
    <SucursalForm
      v-if="showForm"
      :item="selectedItem"
      :is-edit="isEdit"
      @saved="onSaved"
      @close="showForm = false"
    />
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import sucursalService from './sucursal.service';
import authService from '@/services/auth';
import { showSyncToast, showSyncError } from '@/shared/utils/toast';
import SucursalForm from './SucursalForm.vue';

export default {
  name: 'SucursalList',
  components: { SucursalForm },
  data() {
    return {
      items: [],
      filteredItems: [],
      searchText: '',
      activoFilter: '',
      loading: false,
      syncing: false,
      showForm: false,
      selectedItem: null,
      isEdit: false,
      currentPage: 1,
      pageSize: 25,
      sortField: 'Clave',
      sortAsc: true
    };
  },
  computed: {
    totalFiltered() { return this.filteredItems.length; },
    totalPages() { return Math.ceil(this.totalFiltered / this.pageSize) || 1; },
    pagedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredItems.slice(start, start + this.pageSize);
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      let start = Math.max(1, current - 2);
      let end = Math.min(total, current + 2);
      if (end - start < 4) {
        if (start === 1) end = Math.min(total, start + 4);
        else start = Math.max(1, end - 4);
      }
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  watch: {
    currentPage(val) {
      if (val > this.totalPages) this.currentPage = this.totalPages;
    }
  },
  created() {
    this.loadItems();
  },
  methods: {
    async loadItems() {
      this.loading = true;
      try {
        const empresa = authService.getSelectedEmpresa();
        const result = await sucursalService.listar({ EmpresaId: empresa?.EmpresaId || 0 });
        this.items = Array.isArray(result) ? result : (result?.OData || []);
        this.applyFilters();
      } catch (e) {
        console.error('Error al cargar sucursales:', e);
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      let data = [...this.items];
      const q = this.searchText.toLowerCase().trim();
      if (q) {
        data = data.filter(i =>
          (i.Clave || '').toLowerCase().includes(q) ||
          (i.Nombre || '').toLowerCase().includes(q)
        );
      }
      if (this.activoFilter !== '') {
        const activo = this.activoFilter === '1';
        data = data.filter(i => !!i.EsActivo === activo);
      }
      data.sort((a, b) => {
        let va = a[this.sortField];
        let vb = b[this.sortField];
        if (va == null) va = '';
        if (vb == null) vb = '';
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va < vb) return this.sortAsc ? -1 : 1;
        if (va > vb) return this.sortAsc ? 1 : -1;
        return 0;
      });
      this.filteredItems = data;
      this.currentPage = 1;
    },
    sortBy(field) {
      if (this.sortField === field) this.sortAsc = !this.sortAsc;
      else { this.sortField = field; this.sortAsc = true; }
      this.applyFilters();
    },
    sortIcon(field) {
      if (this.sortField !== field) return 'bi bi-chevron-expand mod-sort-inactive';
      return this.sortAsc ? 'bi bi-chevron-up' : 'bi bi-chevron-down';
    },
    openForm(item = null) {
      this.isEdit = !!item;
      this.selectedItem = item ? { ...item } : null;
      this.showForm = true;
    },
    onSaved() {
      this.showForm = false;
      this.loadItems();
    },
    async confirmDelete(item) {
      if (!confirm(`Eliminar sucursal "${item.Nombre}"?`)) return;
      try {
        const result = await sucursalService.borrar(item);
        if (result.Number >= 0) {
          this.loadItems();
        } else {
          alert(result.Message || 'Error al eliminar');
        }
      } catch (e) {
        console.error('Error al eliminar:', e);
        alert('Error al eliminar');
      }
    },
    async sincronizarErp() {
      this.syncing = true;
      try {
        const result = await sucursalService.sincronizarErp();
        showSyncToast(result);
        if (result.Number >= 0) {
          await this.loadItems();
        }
      } catch (e) {
        console.error('Error al sincronizar:', e);
        showSyncError('Error de conexión al sincronizar');
      } finally {
        this.syncing = false;
      }
    },
    exportExcel() {
      const data = this.filteredItems.map(i => ({
        Clave: i.Clave,
        Nombre: i.Nombre,
        Predeterminada: i.EsDefault ? 'Si' : 'No',
        Activo: i.EsActivo ? 'Si' : 'No'
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const colWidths = Object.keys(data[0] || {}).map(k => ({
        wch: Math.max(k.length, ...data.map(r => String(r[k] || '').length)) + 2
      }));
      ws['!cols'] = colWidths;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sucursales');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'sucursales.xlsx');
    }
  }
};
</script>

<style scoped>
@import "@/modules/_shared/catalog.css";
</style>
